import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { requestChangeEmail, update } from '../services/auth.service';
import Button from './Button';
import './EditDetails.scss';
import Feedback from './Feedback';
import { ReactComponent as ArrowIcon } from '../assets/arrow_forward_large.svg';
import { useNavigate } from 'react-router-dom';

const EditDetails: React.FC = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [originalEmailAddress, setOriginalEmailAddress] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [loadingName, setLoadingName] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [nameErrors, setNameErrors] = useState([] as string[]);
  const [nameMessage, setNameMessage] = useState('');
  const [emailErrors, setEmailErrors] = useState([] as string[]);
  const [emailMessage, setEmailMessage] = useState('');

  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);

  const toggleEditName = () => {
    setNameMessage('');
    setNameErrors([]);
    setIsEditingName(true);
  };
  const toggleEditEmail = () => {
    setEmailMessage('');
    setEmailErrors([]);
    setIsEditingEmail(true);
  };

  const { refresh, userInfo, accountLevel } = useAuth();
  const navigate = useNavigate();

  const handleUpdateName = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoadingName(true);
    setNameErrors([]);
    const result = await update(firstName, lastName);
    if (result.success) {
      refresh();
      setIsEditingName(false);
      setNameMessage('Your name has been updated');
      setNameErrors([]);
    } else {
      setNameMessage('');
      setNameErrors(result.errors);
    }
    setLoadingName(false);
  };

  const handleUpdateEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (emailAddress === originalEmailAddress) {
      setEmailErrors(['Please provide a different email address']);
      return;
    }

    setLoadingEmail(true);
    setEmailErrors([]);
    const result = await requestChangeEmail(emailAddress, password);
    if (result.success) {
      refresh();
      setIsEditingEmail(false);
      setEmailMessage(
        `Instructions on how to change your email address have been emailed to ${emailAddress}`
      );
      setPassword('');
      setOriginalEmailAddress(emailAddress);
      setEmailAddress('');
      setEmailErrors([]);
    } else {
      setEmailMessage('');
      setEmailErrors(result.errors);
    }
    setLoadingEmail(false);
  };

  const getInfo = async () => {
    try {
      const info = userInfo;
      setFirstName(info?.firstName ?? '');
      setLastName(info?.lastName ?? '');
      setOriginalEmailAddress(info?.emailAddress ?? '');
      setEmailAddress('');
    } catch {
      setNameErrors(['There was a problem loading your account']);
    }
  };

  useEffect(() => {
    getInfo();
  }, [userInfo]);

  return (
    <>
      <div className="display-area">
        <div className="display-area-section">
          <div className="display-header">
            <label>Name</label>
          </div>
          <div className="display-area-section__body">
            {!isEditingName && (
              <>
                <span className="display-area-section__value">
                  {firstName} {lastName}
                </span>
                <Feedback message={nameMessage} type="success" />
              </>
            )}
            {isEditingName && (
              <form onSubmit={handleUpdateName} className="content-form">
                <div className="field">
                  <label htmlFor="first-name">First Name</label>
                  <input
                    id="first-name"
                    type="text"
                    placeholder="First Name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <label htmlFor="last-name">Last Name</label>
                </div>
                <div className="field">
                  <input
                    id="last-name"
                    type="text"
                    placeholder="Last Name"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div className="button-container">
                  <Button isLoading={loadingName} isSubmit={true}>
                    Update
                  </Button>
                  <Button
                    type="Outline"
                    onClick={() => setIsEditingName(false)}
                  >
                    Cancel
                  </Button>
                </div>
                <Feedback messages={nameErrors} type="error" />
              </form>
            )}
            {!isEditingName && (
              <Button
                type="Link"
                aria-label="Change Name"
                onClick={() => toggleEditName()}
              >
                Change <ArrowIcon />
              </Button>
            )}
          </div>
        </div>
        <div className="display-area-section">
          <div className="display-header">
            <label>Email Address</label>
          </div>
          <div className="display-area-section__body">
            {!isEditingEmail && (
              <>
                <span className="display-area-section__value">
                  {originalEmailAddress}
                </span>
                {userInfo && userInfo.isSSO && (
                  <span>
                    This account in linked to a Google account, so you
                    can&apos;t change your email address here.
                  </span>
                )}
                <Feedback message={emailMessage} type="success" />
              </>
            )}
            {isEditingEmail && (
              <form onSubmit={handleUpdateEmail} className="content-form">
                <div className="field">
                  <label htmlFor="existing-email">Current Email Address</label>
                  <input
                    id="existing-email"
                    type="email"
                    placeholder="Current Email Address"
                    disabled
                    value={originalEmailAddress}
                  />
                </div>
                <div className="field">
                  <label htmlFor="new-email">New Email Address</label>
                  <input
                    id="new-email"
                    type="email"
                    placeholder="New Email Address"
                    required
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    placeholder="Password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                <div className="button-container">
                  <Button isLoading={loadingEmail} isSubmit={true}>
                    Update
                  </Button>
                  <Button
                    type="Outline"
                    onClick={() => setIsEditingEmail(false)}
                  >
                    Cancel
                  </Button>
                </div>
                <Feedback messages={emailErrors} type="error" />
              </form>
            )}
            {!isEditingEmail && userInfo && !userInfo.isSSO && (
              <Button
                type="Link"
                aria-label="Change Email Address"
                onClick={() => toggleEditEmail()}
              >
                Change <ArrowIcon />
              </Button>
            )}
          </div>
        </div>

        <div className="display-area-section">
          <div className="display-header">
            <label>Account Level</label>
          </div>
          <div className="display-area-section__body">
            <span className="display-area-section__value">{accountLevel}</span>
            <Button
              type="Link"
              aria-label="Change Account Level"
              onClick={() => navigate('/account/subscription')}
            >
              Change <ArrowIcon />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditDetails;
